import { NumberField } from "@base-ui/react/number-field";
import { cva, type VariantProps } from "class-variance-authority";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { flushSync } from "react-dom";

import { cn } from "#/lib/utils";

const containerVariants = cva(
  "flex items-center overflow-hidden rounded-full border-2 border-border bg-background text-foreground focus-within:border-primary focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
  {
    variants: {
      size: {
        sm: "h-8 w-24",
        md: "h-9 w-28",
        lg: "h-12 w-32",
        xl: "h-[60px] w-full sm:max-w-[460px]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const valueVariants = cva(
  "min-w-0 flex-1 text-center bg-transparent border-0 outline-none text-inherit tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
  {
    variants: {
      size: {
        sm: "text-sm",
        md: "text-sm",
        lg: "text-base font-medium",
        xl: "text-2xl font-bold",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const ICON_SIZE = {
  sm: "size-3",
  md: "size-3",
  lg: "size-4",
  xl: "size-5",
} as const;

const buttonVariants = cva(
  "shrink-0 h-full inline-flex items-center justify-center text-inherit cursor-pointer bg-transparent border-0 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      size: {
        sm: "w-8",
        md: "w-8",
        lg: "w-8",
        xl: "w-12",
      },
    },
    defaultVariants: { size: "md" },
  },
);

interface QuantityStepperProps extends VariantProps<typeof containerVariants> {
  value: number;
  onChange: (qty: number) => void;
  min?: number;
  max?: number;
  className?: string;
}

function QuantityStepper({
  value,
  onChange,
  min = 1,
  max,
  size,
  className,
}: QuantityStepperProps) {
  const sz = size ?? "md";
  // Base UI NumberField is controlled — three rapid clicks all read the same
  // stale `value` prop because React hasn't re-rendered between them. We mirror
  // the value into local state and `flushSync` each change so the next click
  // sees the latest value within the same task. Parents that need to override
  // the value mid-stroke (e.g. reset to 1) should pass `key={value}`.
  const [internal, setInternal] = useState(value);
  const handleChange = (next: number) => {
    flushSync(() => {
      setInternal(next);
    });
    onChange(next);
  };
  return (
    <NumberField.Root
      value={internal}
      onValueChange={(v) => handleChange(v ?? min)}
      min={min}
      max={max}
    >
      <NumberField.Group
        className={cn(containerVariants({ size: sz }), className)}
      >
        <NumberField.Decrement
          aria-label="Decrease quantity"
          className={buttonVariants({ size: sz })}
        >
          <Minus className={ICON_SIZE[sz]} />
        </NumberField.Decrement>
        <NumberField.Input className={valueVariants({ size: sz })} />
        <NumberField.Increment
          aria-label="Increase quantity"
          className={buttonVariants({ size: sz })}
        >
          <Plus className={ICON_SIZE[sz]} />
        </NumberField.Increment>
      </NumberField.Group>
    </NumberField.Root>
  );
}

export { QuantityStepper, type QuantityStepperProps };
